import { useEffect, useRef, useState } from "react";

import { Chip } from "../Chip";
import { useIsMobile } from "../hooks";
import {
  CheckIcon,
  CopyIcon,
  DotsVerticalIcon,
  EditIcon,
  ExternalLinkIcon,
  QRCodeIcon,
} from "../Icons";
import { PopoverMenu } from "../PopoverMenu";
import { TooltipWrapper } from "../Tooltip";
import { Typo } from "../Typo";
import { ViewPerformanceButton } from "../ViewPerformanceButton";
import { QRCodeModal, type QRCodeModalTranslations } from "./QRCodeModal";

export const stripProtocol = (url: string): string =>
  url.replace(/^https?:\/\//, "");

// Collapses a full referral link to "host/...&aff_id=<id>" for display, keeping
// the full URL available (copy + hover tooltip). Falls back to the bare URL
// (protocol stripped) when there is no aff_id or the URL cannot be parsed.
export const formatReferralLinkDisplay = (url: string): string => {
  try {
    const parsed = new URL(url);
    const affId = parsed.searchParams.get("aff_id");
    if (affId) {
      return `${parsed.host}/...&aff_id=${affId}`;
    }
    return stripProtocol(url);
  } catch {
    return stripProtocol(url);
  }
};

export const useHandleCopy = (text: string, onClick?: () => void) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleCopy = async () => {
    onClick?.();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return { copied, handleCopy };
};

export type ReferralLinkCellTranslations = {
  defaultLabel: string;
  defaultTooltip: string;
  linkCopied: string;
  copied: string;
  copyShortLink: string;
  rename: string;
  getQRCode?: string;
  previewLink?: string;
  viewPerformance?: string;
  qrCodeModal?: QRCodeModalTranslations;
};

export const ReferralLinkCopyIconButton = ({
  id,
  link,
  labelCopied,
  onClick,
  isMobile,
}: {
  id: string;
  link: string;
  labelCopied: string;
  onClick?: () => void;
  isMobile?: boolean;
}) => {
  const { copied, handleCopy } = useHandleCopy(link, onClick);

  if (isMobile) {
    return (
      <button
        type="button"
        aria-label="Copy link"
        data-testid="copy-button"
        onClick={handleCopy}
        className="flex h-[32px] w-[32px] items-center justify-center rounded-lg border border-stroke-primary text-text-primary"
      >
        {copied ? (
          <span>
            <CheckIcon className="text-text-positive-default" />
          </span>
        ) : (
          <CopyIcon />
        )}
      </button>
    );
  }

  if (copied) {
    return (
      <TooltipWrapper
        id={`referral-link-tooltip-copied-${id}`}
        text={labelCopied}
        tooltipPlacement="top"
        tooltipTextAlign="center"
        color="blue"
        tooltipContainerClassName="!w-[unset]"
      >
        <button
          type="button"
          aria-label="Link copied"
          data-testid="copied-button"
          onClick={handleCopy}
          className="flex h-[24px] w-[24px] items-center justify-center rounded-lg hover:bg-overlay-opacity-state-hover"
        >
          <span className="text-text-positive-default">
            <CheckIcon />
          </span>
        </button>
      </TooltipWrapper>
    );
  }

  return (
    <button
      type="button"
      aria-label="Copy link"
      data-testid="copy-button"
      onClick={handleCopy}
      className="flex h-[24px] w-[24px] items-center justify-center rounded-lg hover:bg-overlay-opacity-state-hover"
    >
      <span className="text-text-brand-default">
        <CopyIcon />
      </span>
    </button>
  );
};

const ReferralLinkButtonText = ({
  icon,
  text,
  onClick,
}: {
  icon: React.ReactNode;
  text: string;
  onClick?: () => void;
}) => {
  const isMobile = useIsMobile();

  return (
    <button
      onClick={onClick}
      className="flex h-[48px] w-full flex-row items-center justify-stretch gap-2 rounded-lg text-text-primary hover:bg-overlay-opacity-state-hover lg:!h-[unset] lg:p-2"
    >
      <div className="flex h-[32px] w-[32px] items-center justify-center lg:h-[unset] lg:w-[unset]">
        {icon}
      </div>
      <Typo type={`${isMobile ? "labelSM" : "bodySM"}`}>{text}</Typo>
    </button>
  );
};

const ReferralLinkCopyButtonText = ({
  labelCopied,
  labelCopyShortLink,
  link,
  onClick,
  iconSize = 20,
}: {
  labelCopied: string;
  labelCopyShortLink: string;
  link: string;
  onClick?: () => void;
  iconSize?: number;
}) => {
  const { copied, handleCopy } = useHandleCopy(link, onClick);

  return (
    <ReferralLinkButtonText
      icon={
        copied ? (
          <span className="text-text-positive-default">
            <CheckIcon height={iconSize} width={iconSize} />
          </span>
        ) : (
          <CopyIcon height={iconSize} width={iconSize} />
        )
      }
      text={copied ? labelCopied : labelCopyShortLink}
      onClick={handleCopy}
    />
  );
};

export const ReferralLinkMoreOptionsMenu = ({
  link,
  translations,
  onCopy,
  onRename,
  onGetQRCode,
  onPreviewLink,
  onMenuOpen,
  isMobile,
  onClick,
}: {
  link: string;
  translations: ReferralLinkCellTranslations;
  onCopy?: () => void;
  onRename?: () => void;
  onGetQRCode?: () => void;
  onPreviewLink?: () => void;
  onMenuOpen?: () => void;
  isMobile?: boolean;
  onClick?: () => void;
}) => {
  if (isMobile) {
    return (
      <button
        type="button"
        aria-label="More options"
        className="flex h-[32px] w-[32px] items-center justify-center rounded-lg border border-stroke-primary text-text-primary"
        onClick={() => {
          onMenuOpen?.();
          onClick?.();
        }}
      >
        <DotsVerticalIcon className="h-4 w-4" />
      </button>
    );
  }

  return (
    <PopoverMenu
      asChild
      content={
        <div className="flex w-[200px] flex-col items-start justify-stretch p-4">
          <ReferralLinkCopyButtonText
            labelCopied={translations.copied}
            labelCopyShortLink={translations.copyShortLink}
            link={link}
            onClick={onCopy}
            iconSize={16}
          />
          {onGetQRCode && translations.getQRCode && (
            <ReferralLinkButtonText
              icon={<QRCodeIcon width={16} height={16} />}
              text={translations.getQRCode}
              onClick={onGetQRCode}
            />
          )}

          {onRename && (
            <ReferralLinkButtonText
              icon={<EditIcon width={16} height={16} />}
              text={translations.rename}
              onClick={onRename}
            />
          )}
          {onPreviewLink && translations.previewLink && (
            <ReferralLinkButtonText
              icon={<ExternalLinkIcon width={16} height={16} />}
              text={translations.previewLink}
              onClick={onPreviewLink}
            />
          )}
        </div>
      }
    >
      <button
        type="button"
        aria-label="More options"
        className="flex h-[24px] w-[24px] items-center justify-center rounded-lg text-text-brand-default hover:bg-overlay-opacity-state-hover"
        onClick={onMenuOpen}
      >
        <DotsVerticalIcon className="h-4 w-4" />
      </button>
    </PopoverMenu>
  );
};

export const ReferralLinkCell = ({
  id,
  name,
  isDefault,
  shortLink,
  fullLink,
  translations,
  onCopyIconClick,
  onMenuOpen,
  onCopy,
  onGetQRCode,
  onRename,
  onPreviewLink,
  performanceHref,
  onViewPerformance,
  onNavigateToPerformance,
}: {
  id: string;
  name?: string;
  isDefault?: boolean;
  shortLink: string;
  fullLink?: string;
  translations: ReferralLinkCellTranslations;
  onCopyIconClick?: () => void;
  onMenuOpen?: () => void;
  onCopy?: () => void;
  onGetQRCode?: () => void;
  onRename?: () => void;
  onPreviewLink?: () => void;
  performanceHref?: string;
  onViewPerformance?: () => void;
  onNavigateToPerformance?: (href: string) => void;
}) => {
  const displayLink = fullLink || shortLink || "";
  const [isQROpen, setIsQROpen] = useState(false);

  const handleGetQRCode = translations.qrCodeModal
    ? () => {
        onGetQRCode?.();
        document.dispatchEvent(new MouseEvent("mousedown"));
        setIsQROpen(true);
      }
    : undefined;

  const handlePreviewLink =
    onPreviewLink ??
    (translations.previewLink
      ? () => {
          const url = fullLink || shortLink;
          if (url) window.open(url, "_blank", "noopener,noreferrer");
        }
      : undefined);

  return (
    <>
      <div className="flex h-[80px] min-w-0 flex-col justify-center gap-2 p-4">
        <div className="flex min-w-0 flex-row items-center justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-1 overflow-hidden">
            {(name || isDefault) && (
              <div className="flex min-w-0 items-center gap-2">
                {name && (
                  <Typo type="bodyMD" className="truncate">
                    {name}
                  </Typo>
                )}
                {isDefault && (
                  <TooltipWrapper
                    id={`referral-link-default-${id}`}
                    text={translations.defaultTooltip}
                    color="blue"
                    tooltipPlacement="top"
                    positionStrategy="fixed"
                    openEvents={{ click: true }}
                  >
                    <button
                      type="button"
                      aria-describedby={`referral-link-default-${id}`}
                      className="inline-flex shrink-0 appearance-none border-0 bg-transparent p-0"
                    >
                      <Chip>{translations.defaultLabel}</Chip>
                    </button>
                  </TooltipWrapper>
                )}
              </div>
            )}
            <TooltipWrapper
              id={`referral-link-full-${id}`}
              text={displayLink}
              color="blue"
              tooltipPlacement="top"
              positionStrategy="fixed"
              tooltipMaxWidth={360}
              tooltipContainerClassName="!break-all"
            >
              <Typo
                type="bodySM"
                textColor="text-text-secondary"
                className="truncate"
              >
                {formatReferralLinkDisplay(displayLink)}
              </Typo>
            </TooltipWrapper>
          </div>
          <div className="flex shrink-0 flex-row gap-2">
            <ReferralLinkCopyIconButton
              id={id}
              labelCopied={translations.linkCopied}
              link={displayLink}
              onClick={onCopyIconClick}
            />
            {performanceHref && translations.viewPerformance && (
              <ViewPerformanceButton
                id={id}
                href={performanceHref}
                label={translations.viewPerformance}
                onClick={onViewPerformance}
                onNavigate={onNavigateToPerformance}
              />
            )}
            <ReferralLinkMoreOptionsMenu
              translations={translations}
              link={shortLink || fullLink || ""}
              onMenuOpen={onMenuOpen}
              onCopy={onCopy}
              onGetQRCode={handleGetQRCode}
              onRename={onRename}
              onPreviewLink={handlePreviewLink}
            />
          </div>
        </div>
      </div>
      {isQROpen && translations.qrCodeModal && (
        <QRCodeModal
          isOpen={isQROpen}
          onClose={() => setIsQROpen(false)}
          url={displayLink}
          translations={translations.qrCodeModal}
        />
      )}
    </>
  );
};
